import { DataEmFormatoISO, Empregado, ID } from '@/tipos'
import moment from 'moment'
import { multiSelect } from 'src/common/selectors'
import { IFeriasCalendario, TipoDeFeriasCalendarioEnum } from '../../dominio/gerenciamentoDeFerias.interface'
import { ActionContext } from 'vuex'
import { AcoesSobreSolicitacaoEnum } from '@/typescript/modulos/governanca/solicitacoes/dominio/solicitacoes.enum'
import i18n from '@/typescript/servicos/i18n/i18n.servico'
import {
  montarLinhaTabelaComAviso,
  montarLinhaTabelaComAviso30Dias,
} from "./htmlCustomizadoGantt";

type EmpregadoFiltro = Pick<Empregado, 'id' | 'code' | 'name' | 'resignation'>

export type EmpregadoComFerias = {
  id: ID,
  nome: string,
  temAviso: boolean,
  dataVencimentoPrazoConcessivo?: DataEmFormatoISO,
  diasDeFeriasAVencer?: number
  menosTrintaDiasParaVencer?: boolean
}

export type EmpregadoGantt = {
  id: string;
  label?: string;
  classes: string;
  iconClass?: string;
  tooltip?: string | object;
  headerHtml?: string;
}

export type FeriasGantt = {
  id: string;
  resourceId: string;
  label: string;
  legenda: string;
  from: number;
  to: number;
  classes: string[];
  draggable: boolean;
  resizable: boolean;
  html?: string;
  sugestaoDoAgente?: string | null;
}
export interface IStateCalendarioDeFerias {
  dataInicio: Date
  dataFim: Date
  employees: {
    loaded: Array<EmpregadoFiltro>
    selected: Array<EmpregadoFiltro>
  },
  /**
   * Solicitações de férias agendadas
   * e pendentes do calendario de férias
   */
  solicitacoesFerias: Array<IFeriasCalendario>
  empregadosComFerias: Array<EmpregadoComFerias>
  estaCarregandoCalendarioDeFerias: boolean
  estaCarregandoAgente: boolean
  mensagemDeErro: string | null
  quantidadeEmpregadosComFeriasAVencer: Array<Empregado>
  quantidadeEmpregadosComFeriasPendentes: Array<Empregado>
  quantidadeEmpregadosComFeriasAgendadas: Array<Empregado>
}

const estadoPadrao = () => {
  return {
    ...multiSelect.mapStates(['employees']),
    dataInicio: null,
    dataFim: null,
    solicitacoesFerias: [],
    empregadosComFerias: [],
    estaCarregandoCalendarioDeFerias: false,
    estaCarregandoAgente: false,
    mensagemDeErro: null,
    quantidadeEmpregadosComFeriasAVencer: [],
    quantidadeEmpregadosComFeriasPendentes: [],
    quantidadeEmpregadosComFeriasAgendadas: [],
  }
}

const state = estadoPadrao()

const mutations = {
  ...multiSelect.mapMutations(['employees']),
  dataInicio: (state: IStateCalendarioDeFerias, payload: Date) => { state.dataInicio = moment(payload).startOf('month').toDate() },
  dataFim: (state: IStateCalendarioDeFerias, payload: Date) => { state.dataFim = moment(payload).endOf('month').toDate() },
  adicionarSolicitacoesFerias: (state: IStateCalendarioDeFerias, payload: Array<IFeriasCalendario>) => { state.solicitacoesFerias.push(...payload) },
  setEmpregadosComFerias: (state: IStateCalendarioDeFerias, payload: Array<EmpregadoComFerias>) => {
    const arrayCompleto = Array.from([...state.empregadosComFerias, ...payload])
    state.empregadosComFerias = Array.from(new Map(arrayCompleto.map(empregado => [empregado.id, empregado])).values())
  },
  limparEmpregadosComFerias: (state: IStateCalendarioDeFerias) => {
    state.empregadosComFerias = []
  },
  limparSolicitacoesFerias: (state: IStateCalendarioDeFerias) => {
    state.solicitacoesFerias = []
  },
  setEstaCarregandoCalendarioDeFerias: (state: IStateCalendarioDeFerias, payload: boolean) => {
    state.estaCarregandoCalendarioDeFerias = payload
  },
  setEstaCarregandoAgente: (state: IStateCalendarioDeFerias, payload: boolean) => {
    state.estaCarregandoAgente = payload
  },
  setMensagemDeErro: (state: IStateCalendarioDeFerias, payload: string | null) => {
    state.mensagemDeErro = payload
  },
  limparMensagemDeErro: (state: IStateCalendarioDeFerias) => {
    state.mensagemDeErro = null
  },
  setQuantidadeEmpregadosComFeriasAVencer: (state: IStateCalendarioDeFerias, payload: Empregado[]) => {
    const arrayCompleto = Array.from([...state.quantidadeEmpregadosComFeriasAVencer, ...payload])
    state.quantidadeEmpregadosComFeriasAVencer = Array.from(new Map(arrayCompleto.map(empregado => [empregado.id, empregado])).values())
  },
  setQuantidadeEmpregadosComFeriasPendentes: (state: IStateCalendarioDeFerias, payload: Empregado[]) => {
    const arrayCompleto = Array.from([...state.quantidadeEmpregadosComFeriasPendentes, ...payload])
    state.quantidadeEmpregadosComFeriasPendentes = Array.from(new Map(arrayCompleto.map(empregado => [empregado.id, empregado])).values())
  },
  setQuantidadeEmpregadosComFeriasAgendadas: (state: IStateCalendarioDeFerias, payload: Empregado[]) => {
    const arrayCompleto = Array.from([...state.quantidadeEmpregadosComFeriasAgendadas, ...payload])
    state.quantidadeEmpregadosComFeriasAgendadas = Array.from(new Map(arrayCompleto.map(empregado => [empregado.id, empregado])).values())
  },
  limparEstatisticasDeEmpregados: (state: IStateCalendarioDeFerias) => {
    state.quantidadeEmpregadosComFeriasAVencer = []
    state.quantidadeEmpregadosComFeriasPendentes = []
    state.quantidadeEmpregadosComFeriasAgendadas = []
  }
}

const getters = {
  ocorreuErroAoBuscarDadosCalendarioDeFerias: (state: IStateCalendarioDeFerias) => {
    return state.mensagemDeErro !== null
  },
  calendarioVazio: (state: IStateCalendarioDeFerias) => {
    return state.empregadosComFerias.length === 0 || state.solicitacoesFerias.length === 0
  },
  quantidadeEmpregadosComFeriasAVencer: (state: IStateCalendarioDeFerias): number => {
    return state.quantidadeEmpregadosComFeriasAVencer.length
  },
  quantidadeEmpregadosComFeriasPendentes: (state: IStateCalendarioDeFerias): number => {
    return state.quantidadeEmpregadosComFeriasPendentes.length
  },
  quantidadeEmpregadosComFeriasAgendadas: (state: IStateCalendarioDeFerias): number => {
    return state.quantidadeEmpregadosComFeriasAgendadas.length
  },
  /**
   * Objeto que vai direto para o gantt chart
   */
  empregadosGantt: (state: IStateCalendarioDeFerias): Array<EmpregadoGantt> => {
    const empregados = state.empregadosComFerias.map((empregado: EmpregadoComFerias) => {
      // Caso não tenha aviso, criar a linha normalmente
      if (empregado.temAviso === false) {
        return {
          id: empregado.id,
          label: empregado.nome,
          classes: "tabela-linha",
          iconClass: "",
        };
      }

      let headerHtml = "";

      const tooltip = i18n.t(
        "gerenciamentoDeFerias.calendarioDeFerias.tooltipPrazoParaAgendar",
        {
          dias: empregado.diasDeFeriasAVencer,
          data: moment(empregado.dataVencimentoPrazoConcessivo).format('DD.MM.YYYY')
        }
      );

      if (empregado.menosTrintaDiasParaVencer) {
        headerHtml = montarLinhaTabelaComAviso30Dias({texto: empregado.nome, empregadoId: empregado.id });
      } else {
        headerHtml = montarLinhaTabelaComAviso({texto: empregado.nome, empregadoId: empregado.id });
      }

      // Caso tenha aviso, criar a linha com o tooltip
      return {
        id: empregado.id,
        classes: "tabela-linha",
        tooltip,
        headerHtml,
      };
    });

    const ordenarPorDataVencimentoEFerias = (a: { id: string }, b: { id: string }) => {
      // Busca os empregados completos para acessar dataVencimentoPrazoConcessivo
      const empregadoA = state.empregadosComFerias.find(emp => emp.id === a.id);
      const empregadoB = state.empregadosComFerias.find(emp => emp.id === b.id);

      // 1. Ordenação primária por dataVencimentoPrazoConcessivo (mais próxima da data atual)
      const dataAtual = moment();

      if (empregadoA?.dataVencimentoPrazoConcessivo && empregadoB?.dataVencimentoPrazoConcessivo) {
        const dataVencimentoA = moment(empregadoA.dataVencimentoPrazoConcessivo);
        const dataVencimentoB = moment(empregadoB.dataVencimentoPrazoConcessivo);

        const diferencaA = Math.abs(dataVencimentoA.diff(dataAtual, 'days'));
        const diferencaB = Math.abs(dataVencimentoB.diff(dataAtual, 'days'));

        if (diferencaA !== diferencaB) {
          return diferencaA - diferencaB; // Menor diferença primeiro
        }
      } else if (empregadoA?.dataVencimentoPrazoConcessivo && !empregadoB?.dataVencimentoPrazoConcessivo) {
        return -1; // A tem data, B não tem - A vem primeiro
      } else if (!empregadoA?.dataVencimentoPrazoConcessivo && empregadoB?.dataVencimentoPrazoConcessivo) {
        return 1; // B tem data, A não tem - B vem primeiro
      }

      // 2. Ordenação secundária por férias (mantém a lógica existente)
      const feriasDoEmpregadoA = state.solicitacoesFerias
        .filter(ferias => ferias.idEmpregado === a.id)
        .sort((ferias1, ferias2) => ferias1.inicio - ferias2.inicio);

      const feriasDoEmpregadoB = state.solicitacoesFerias
        .filter(ferias => ferias.idEmpregado === b.id)
        .sort((ferias1, ferias2) => ferias1.inicio - ferias2.inicio);

      // Pega a férias mais próxima (primeira do array ordenado)
      const feriasMaisProximaA = feriasDoEmpregadoA[0];
      const feriasMaisProximaB = feriasDoEmpregadoB[0];

      // Se um empregado não tem férias, ele vai para o final
      if (!feriasMaisProximaA && !feriasMaisProximaB) {
        return 0; // Mantém ordem original se ambos não têm férias
      }
      if (!feriasMaisProximaA) return 1; // A vai para o final
      if (!feriasMaisProximaB) return -1; // B vai para o final

      // Ordena por data de início das férias mais próximas (crescente)
      return feriasMaisProximaA.inicio - feriasMaisProximaB.inicio;
    }

    // Ordena os empregados baseado na data de vencimento e depois por férias
    empregados.sort(ordenarPorDataVencimentoEFerias);

    return empregados
  },
  /**
   * Objeto que vai direto para o gantt chart
   */
  feriasGantt: (state: IStateCalendarioDeFerias): Array<FeriasGantt> => {
    const tasks = state.solicitacoesFerias.map((solicitacao) => {
      const task: FeriasGantt = {
        id: solicitacao.id,
        resourceId: solicitacao.idEmpregado,
        label: '',
        from: solicitacao.inicio,
        to: solicitacao.fim,
        legenda: solicitacao.legenda,
        classes: [solicitacao.tipo, "item-ferias"],
        draggable: false,
        resizable: false,
        sugestaoDoAgente: null,
      };

      if (solicitacao.tipo === TipoDeFeriasCalendarioEnum.sugestaoDoAgenteAprovar) {
        task.sugestaoDoAgente = 'APROVAR'
      } else if (solicitacao.tipo === TipoDeFeriasCalendarioEnum.sugestaoDoAgenteReprovar) {
        task.sugestaoDoAgente = 'REPROVAR'
      }

      return task;
    });

    return tasks;
  }
}

const actions = {
  limparCalendarioDeFerias: ({ commit }: ActionContext<IStateCalendarioDeFerias, unknown>) => {
    commit('limparEmpregadosComFerias')
    commit('limparSolicitacoesFerias')
    commit('limparEstatisticasDeEmpregados')
  },
  pegarSolicitacaoFeriasPorId: ({ state }: ActionContext<IStateCalendarioDeFerias, unknown>, payload: ID) => {
    return state.solicitacoesFerias.find(solicitacao => solicitacao.id === payload)
  },
  atualizarFeriasDoCalendario: ({ commit, state }: ActionContext<IStateCalendarioDeFerias, unknown>, payload: { acao: AcoesSobreSolicitacaoEnum.Aprovar | AcoesSobreSolicitacaoEnum.Reprovar, id: ID }) => {
    const indexSolicitacaoAlterada = state.solicitacoesFerias.findIndex(solicitacao => solicitacao.id === payload.id)
    const todasSolicitacoes = state.solicitacoesFerias

    if (indexSolicitacaoAlterada === -1) return

    if (payload.acao === AcoesSobreSolicitacaoEnum.Aprovar) {
      todasSolicitacoes[indexSolicitacaoAlterada].tipo = TipoDeFeriasCalendarioEnum.agendadas
    } else {
      todasSolicitacoes.splice(indexSolicitacaoAlterada, 1)
    }
    commit('limparSolicitacoesFerias')
    commit('adicionarSolicitacoesFerias', todasSolicitacoes)
  },
  pegarEmpregadoPorIdFerias: ({ state }: ActionContext<IStateCalendarioDeFerias, unknown>, idFerias: ID): EmpregadoComFerias | null => {
    const ferias = state.solicitacoesFerias.find(ferias => ferias.id === idFerias)

    if (!ferias) return null

    return state.empregadosComFerias.find(empregado => empregado.id === ferias?.idEmpregado) || null
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}
